import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import KMBenchmarkingPanel from '@/components/capability/KMBenchmarkingPanel';
import { CapabilityAnalyticsView } from '@/components/capability/CapabilityAnalyticsView';
import { CapabilityListView } from '@/components/capability/CapabilityListView';
import { CapabilityHistoryView } from '@/components/capability/CapabilityHistoryView';
import { CapabilityTakeAssessmentView } from '@/components/capability/CapabilityTakeAssessmentView';
import { CapabilityResultsView } from '@/components/capability/CapabilityResultsView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const CapabilityAssessmentPage = () => {
  const { token, user } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDomain, setAnalyticsDomain] = useState(''); // '' = all domains
  const [effectiveness, setEffectiveness] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareType, setShareType] = useState(''); // 'assessment' or 'results'
  const [shareTarget, setShareTarget] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderAssessment, setReminderAssessment] = useState(null);
  const [reminderConfig, setReminderConfig] = useState(null);
  const [completionStatus, setCompletionStatus] = useState(null);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderDeadline, setReminderDeadline] = useState('');
  const [reminderMessage, setReminderMessage] = useState('');
  const [certDialogOpen, setCertDialogOpen] = useState(false);
  const [certUploadName, setCertUploadName] = useState('');
  const [certFile, setCertFile] = useState(null);
  const [certUploading, setCertUploading] = useState(false);
  const [certIssueDate, setCertIssueDate] = useState('');
  const [certExpiryDate, setCertExpiryDate] = useState('');
  const [myCerts, setMyCerts] = useState([]);
  const [allCerts, setAllCerts] = useState([]);
  const [certFilter, setCertFilter] = useState('');
  const [pendingCertsToUpload, setPendingCertsToUpload] = useState([]);
  const [datesDialogOpen, setDatesDialogOpen] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [editIssueDate, setEditIssueDate] = useState('');
  const [editExpiryDate, setEditExpiryDate] = useState('');
  const [certSchedule, setCertSchedule] = useState(null);
  const [certScheduleSaving, setCertScheduleSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchAssessments(); fetchMySubmissions(); fetchMyCerts(); }, []);

  const fetchAssessments = async () => {
    try { const res = await axios.get(`${API}/assessments`, { headers }); setAssessments(res.data); }
    catch {} finally { setLoading(false); }
  };

  const fetchMySubmissions = async () => {
    try { const res = await axios.get(`${API}/assessment-submissions/my`, { headers }); setSubmissions(res.data); } catch {}
  };

  const fetchAnalytics = async (domainOverride) => {
    setAnalyticsLoading(true);
    try {
      const dom = domainOverride !== undefined ? domainOverride : analyticsDomain;
      const res = await axios.get(`${API}/assessment-analytics`, { headers, params: { email_domain: dom || '' } });
      setAnalytics(res.data); setView('analytics'); fetchAllCerts(); fetchCertSchedule();
      try {
        const eff = await axios.get(`${API}/assessment-reminders/effectiveness?days=30`, { headers });
        setEffectiveness(eff.data);
      } catch { /* non-blocking */ }
    }
    catch { toast.error('Failed to load analytics'); }
    finally { setAnalyticsLoading(false); }
  };

  const changeAnalyticsDomain = async (dom) => {
    setAnalyticsDomain(dom);
    await fetchAnalytics(dom);
  };

  const startAssessment = (a) => { setSelectedAssessment(a); setCurrentSection(0); setAnswers({}); setResult(null); setView('take'); };

  const handleAnswer = (sectionId, questionId, value) => { setAnswers(prev => ({ ...prev, [`${sectionId}_${questionId}`]: value })); };

  const openShareAssessment = (assessment) => { setShareType('assessment'); setShareTarget(assessment); setShareDialogOpen(true); };
  const openShareResults = (submission) => { setShareType('results'); setShareTarget(submission); setShareDialogOpen(true); };
  const openShareAnalytics = () => { setShareType('analytics'); setShareTarget({ assessment_id: 'km_self_assessment_v1' }); setShareDialogOpen(true); };

  const [downloadingAnalytics, setDownloadingAnalytics] = useState(null); // 'csv' | 'pdf' | null
  const [publicLinkDialogOpen, setPublicLinkDialogOpen] = useState(false);
  const [publicLinkExpiry, setPublicLinkExpiry] = useState(7);
  const [creatingPublicLink, setCreatingPublicLink] = useState(false);
  const [lastPublicLink, setLastPublicLink] = useState(null); // {public_url, expires_at, snapshot_id}
  const [myPublicLinks, setMyPublicLinks] = useState([]);
  const [loadingPublicLinks, setLoadingPublicLinks] = useState(false);

  const loadMyPublicLinks = async () => {
    setLoadingPublicLinks(true);
    try {
      const res = await axios.get(`${API}/assessment-analytics/public-links`, { headers });
      setMyPublicLinks(res.data || []);
    } catch (_) { /* non-fatal */ }
    finally { setLoadingPublicLinks(false); }
  };

  const openPublicLinkDialog = async () => {
    setLastPublicLink(null);
    setPublicLinkExpiry(7);
    setPublicLinkDialogOpen(true);
    await loadMyPublicLinks();
  };

  const createPublicLink = async () => {
    setCreatingPublicLink(true);
    try {
      const res = await axios.post(`${API}/assessment-analytics/public-link`, {
        assessment_id: 'km_self_assessment_v1',
        expiry_days: publicLinkExpiry,
        email_domain: analyticsDomain || '',
      }, { headers });
      setLastPublicLink(res.data);
      // Copy to clipboard best-effort
      try { await navigator.clipboard.writeText(res.data.public_url); toast.success(`Public link created & copied (valid ${publicLinkExpiry} day${publicLinkExpiry === 1 ? '' : 's'})`); }
      catch { toast.success(`Public link created (valid ${publicLinkExpiry} day${publicLinkExpiry === 1 ? '' : 's'})`); }
      await loadMyPublicLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create public link');
    } finally { setCreatingPublicLink(false); }
  };

  const revokePublicLink = async (snapshot_id) => {
    if (!window.confirm('Revoke this public link? Anyone holding it will lose access immediately.')) return;
    try {
      await axios.delete(`${API}/assessment-analytics/public-link/${snapshot_id}`, { headers });
      toast.success('Public link revoked');
      await loadMyPublicLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke');
    }
  };

  const downloadAnalytics = async (fmt) => {
    setDownloadingAnalytics(fmt);
    try {
      const res = await axios.get(`${API}/assessment-analytics/export.${fmt}?assessment_id=km_self_assessment_v1&email_domain=${encodeURIComponent(analyticsDomain || '')}`, {
        headers, responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: fmt === 'pdf' ? 'application/pdf' : 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().substring(0, 10).replace(/-/g, '');
      const tag = analyticsDomain ? `-${analyticsDomain}` : '';
      a.href = url; a.download = `team-assessment-analytics${tag}-${stamp}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded${analyticsDomain ? ` (${analyticsDomain})` : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to download ${fmt.toUpperCase()}`);
    } finally { setDownloadingAnalytics(null); }
  };

  const handleShareSend = async (emails, message) => {
    setSharing(true);
    try {
      if (shareType === 'assessment' && shareTarget) {
        await axios.post(`${API}/assessments/${shareTarget.assessment_id}/share`, { to_emails: emails, message }, { headers });
        toast.success(`Assessment invitation sent to ${emails.length} recipient(s)`);
      } else if (shareType === 'results' && shareTarget) {
        await axios.post(`${API}/assessment-submissions/${shareTarget.submission_id}/share-results`, { to_emails: emails, message }, { headers });
        toast.success(`Results shared with ${emails.length} recipient(s)`);
      } else if (shareType === 'analytics') {
        const res = await axios.post(`${API}/assessment-analytics/share`, {
          to_emails: emails, message,
          assessment_id: shareTarget?.assessment_id || 'km_self_assessment_v1',
          email_domain: analyticsDomain || '',
          attach_pdf: true, attach_csv: true,
        }, { headers });
        toast.success(`Analytics snapshot sent to ${res.data.sent_count} recipient(s)${res.data.failed_count ? ` · ${res.data.failed_count} failed` : ''}`);
      }
      setShareDialogOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send'); }
    finally { setSharing(false); }
  };

  const openReminderTracker = async (assessment) => {
    setReminderAssessment(assessment);
    setReminderLoading(true);
    setReminderDialogOpen(true);
    try {
      const [cfg, sts] = await Promise.all([
        axios.get(`${API}/assessments/${assessment.assessment_id}/reminders`, { headers }),
        axios.get(`${API}/assessments/${assessment.assessment_id}/reminders/status`, { headers }),
      ]);
      setReminderConfig(cfg.data);
      setCompletionStatus(sts.data);
      setReminderDeadline(cfg.data.deadline || '');
      setReminderMessage(cfg.data.reminder_message || '');
    } catch { toast.error('Failed to load reminder data'); }
    finally { setReminderLoading(false); }
  };

  const saveReminderConfig = async () => {
    if (!reminderAssessment) return;
    try {
      await axios.put(`${API}/assessments/${reminderAssessment.assessment_id}/reminders`, {
        enabled: true, deadline: reminderDeadline, reminder_message: reminderMessage,
      }, { headers });
      toast.success('Reminder settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const sendReminders = async () => {
    if (!reminderAssessment) return;
    setSendingReminder(true);
    try {
      const res = await axios.post(`${API}/assessments/${reminderAssessment.assessment_id}/reminders/send`, {
        message: reminderMessage,
      }, { headers });
      toast.success(res.data.message);
      // Refresh status
      const sts = await axios.get(`${API}/assessments/${reminderAssessment.assessment_id}/reminders/status`, { headers });
      setCompletionStatus(sts.data);
      const cfg = await axios.get(`${API}/assessments/${reminderAssessment.assessment_id}/reminders`, { headers });
      setReminderConfig(cfg.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send reminders'); }
    finally { setSendingReminder(false); }
  };

  const KM_CERTS = ['CKM', 'CKMS', 'CKMA', 'CPKM', 'CKME'];

  const fetchMyCerts = async () => {
    try { const res = await axios.get(`${API}/certifications/my`, { headers }); setMyCerts(res.data); } catch {}
  };

  const fetchAllCerts = async () => {
    try {
      const params = certFilter ? { status: certFilter } : {};
      const res = await axios.get(`${API}/certifications`, { params, headers });
      setAllCerts(res.data);
    } catch {}
  };

  const handleCertUpload = async () => {
    if (!certUploadName || !certFile) { toast.error('Select a certification and file'); return; }
    setCertUploading(true);
    try {
      const formData = new FormData();
      formData.append('certification_name', certUploadName);
      formData.append('file', certFile);
      if (certIssueDate) formData.append('issue_date', certIssueDate);
      if (certExpiryDate) formData.append('expiry_date', certExpiryDate);
      await axios.post(`${API}/certifications/upload`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      toast.success(`${certUploadName} certificate uploaded for review`);
      setCertDialogOpen(false); setCertUploadName(''); setCertFile(null);
      setCertIssueDate(''); setCertExpiryDate('');
      fetchMyCerts();
      // Remove from pending list
      setPendingCertsToUpload(prev => prev.filter(c => c !== certUploadName));
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setCertUploading(false); }
  };

  const openEditDates = (cert) => {
    setEditingCert(cert);
    setEditIssueDate(cert.issue_date || '');
    setEditExpiryDate(cert.expiry_date || '');
    setDatesDialogOpen(true);
  };

  const handleSaveDates = async () => {
    if (!editingCert) return;
    try {
      await axios.put(`${API}/certifications/${editingCert.cert_id}/dates`,
        { issue_date: editIssueDate, expiry_date: editExpiryDate }, { headers });
      toast.success('Dates updated');
      setDatesDialogOpen(false); setEditingCert(null);
      fetchMyCerts(); fetchAllCerts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const fetchCertSchedule = async () => {
    try {
      const res = await axios.get(`${API}/certifications/schedule`, { headers });
      setCertSchedule(res.data);
    } catch {}
  };

  const saveCertSchedule = async (patch) => {
    setCertScheduleSaving(true);
    try {
      const res = await axios.put(`${API}/certifications/schedule`, patch, { headers });
      setCertSchedule(res.data);
      toast.success('Schedule updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setCertScheduleSaving(false); }
  };

  const handleCertReview = async (certId, status, notes = '') => {
    try {
      await axios.put(`${API}/certifications/${certId}/review`, { status, notes }, { headers });
      toast.success(`Certification ${status}`);
      fetchAllCerts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Review failed'); }
  };

  const handleDeleteCert = async (certId) => {
    try { await axios.delete(`${API}/certifications/${certId}`, { headers }); toast.success('Deleted'); fetchMyCerts(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Detect KM certs in submission and prompt
  const detectKmCertsInResult = (res) => {
    if (!res?.answers) return [];
    const eduAnswer = res.answers?.general_info_education || '';
    try {
      const parsed = JSON.parse(eduAnswer);
      if (Array.isArray(parsed)) return parsed.filter(c => KM_CERTS.includes(c));
    } catch {}
    return [];
  };

  const handleRefreshRecs = async (submissionId) => {
    toast.info('Generating recommendations...');
    try {
      const res = await axios.post(`${API}/assessment-submissions/${submissionId}/generate-recommendations`, {}, { headers });
      // Refresh the submission data
      if (result?.submission_id === submissionId) {
        setResult(prev => ({ ...prev, recommendations: res.data.recommendations }));
      }
      if (selectedSubmission?.submission_id === submissionId) {
        setSelectedSubmission(prev => ({ ...prev, recommendations: res.data.recommendations }));
      }
      toast.success('Recommendations generated!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate'); }
  };

  const runExpiryScan = async () => {
    try {
      const res = await axios.post(`${API}/certifications/check-expiries`, {}, { headers });
      const total = (res.data.expired || 0) + (res.data['7'] || 0) + (res.data['30'] || 0) + (res.data['60'] || 0);
      toast.success(total > 0 ? `Dispatched ${total} expiry alert(s)` : 'No new expiry alerts needed');
      fetchAllCerts();
      fetchCertSchedule();
    } catch (e) { toast.error(e.response?.data?.detail || 'Scan failed'); }
  };

  const filterCerts = (f) => {
    setCertFilter(f);
    setTimeout(fetchAllCerts, 0);
  };

  const sections = selectedAssessment?.sections || [];
  const section = sections[currentSection];
  const answeredInSection = section ? section.questions.filter(q => answers[`${section.section_id}_${q.question_id}`]).length : 0;
  const totalInSection = section ? section.questions.length : 0;
  const totalQuestions = sections.reduce((sum, s) => sum + s.questions.length, 0);
  const totalAnswered = Object.keys(answers).length;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/assessments/${selectedAssessment.assessment_id}/submit`, { answers }, { headers });
      setResult(res.data); setView('results'); fetchMySubmissions(); fetchMyCerts();
      toast.success('Assessment submitted successfully!');
      // Detect KM certifications and prompt upload
      const kmCerts = detectKmCertsInResult(res.data);
      if (kmCerts.length > 0) {
        setPendingCertsToUpload(kmCerts);
      }
      // Poll for recommendations (generated async)
      if (!res.data.recommendations) {
        const pollRecs = async (attempts = 0) => {
          if (attempts > 10) return;
          await new Promise(r => setTimeout(r, 3000));
          try {
            const sub = await axios.get(`${API}/assessment-submissions/my`, { headers });
            const latest = sub.data.find(s => s.submission_id === res.data.submission_id);
            if (latest?.recommendations && !latest.recommendations.error) {
              setResult(prev => prev?.submission_id === latest.submission_id ? { ...prev, recommendations: latest.recommendations } : prev);
            } else {
              pollRecs(attempts + 1);
            }
          } catch {}
        };
        pollRecs();
      }
    } catch { toast.error('Failed to submit assessment'); }
    finally { setSubmitting(false); }
  };

  // == BENCHMARKING VIEW ==
  if (view === 'benchmarking') {
    return (
      <div data-testid="capability-benchmarking-page">
        <KMBenchmarkingPanel token={token} onBack={() => setView('list')} />
      </div>
    );
  }

  // == LIST VIEW ==
  if (view === 'list') {
    return (
      <CapabilityListView
        loading={loading}
        assessments={assessments}
        submissions={submissions}
        analyticsLoading={analyticsLoading}
        shareDialogOpen={shareDialogOpen}
        shareType={shareType}
        sharing={sharing}
        reminderDialogOpen={reminderDialogOpen}
        reminderLoading={reminderLoading}
        completionStatus={completionStatus}
        reminderDeadline={reminderDeadline}
        reminderMessage={reminderMessage}
        reminderConfig={reminderConfig}
        sendingReminder={sendingReminder}
        setView={setView}
        setShareDialogOpen={setShareDialogOpen}
        setReminderDialogOpen={setReminderDialogOpen}
        setReminderDeadline={setReminderDeadline}
        setReminderMessage={setReminderMessage}
        onFetchAnalytics={fetchAnalytics}
        onStartAssessment={startAssessment}
        onShareAssessment={openShareAssessment}
        onOpenReminderTracker={openReminderTracker}
        onShareSend={handleShareSend}
        onSaveReminderConfig={saveReminderConfig}
        onSendReminders={sendReminders}
      />
    );
  }

  // == ANALYTICS VIEW ==
  if (view === 'analytics' && analytics) {
    return (
      <CapabilityAnalyticsView
        analytics={analytics}
        analyticsDomain={analyticsDomain}
        effectiveness={effectiveness}
        certSchedule={certSchedule}
        certScheduleSaving={certScheduleSaving}
        certFilter={certFilter}
        allCerts={allCerts}
        downloadingAnalytics={downloadingAnalytics}
        shareDialogOpen={shareDialogOpen}
        shareType={shareType}
        sharing={sharing}
        publicLinkDialogOpen={publicLinkDialogOpen}
        publicLinkExpiry={publicLinkExpiry}
        creatingPublicLink={creatingPublicLink}
        lastPublicLink={lastPublicLink}
        myPublicLinks={myPublicLinks}
        loadingPublicLinks={loadingPublicLinks}
        certDownloadBaseUrl={`${API}/certifications/download`}
        setView={setView}
        setShareDialogOpen={setShareDialogOpen}
        setPublicLinkDialogOpen={setPublicLinkDialogOpen}
        setPublicLinkExpiry={setPublicLinkExpiry}
        onDownloadAnalytics={downloadAnalytics}
        onOpenPublicLinkDialog={openPublicLinkDialog}
        onOpenShareAnalytics={openShareAnalytics}
        onChangeDomain={changeAnalyticsDomain}
        onSaveCertSchedule={saveCertSchedule}
        onFilterCerts={filterCerts}
        onRunExpiryScan={runExpiryScan}
        onCertReview={handleCertReview}
        onShareSend={handleShareSend}
        onCreatePublicLink={createPublicLink}
        onLoadMyPublicLinks={loadMyPublicLinks}
        onRevokePublicLink={revokePublicLink}
      />
    );
  }



  // == HISTORY VIEW ==
  if (view === 'history') {
    return (
      <CapabilityHistoryView
        submissions={submissions}
        selectedSubmission={selectedSubmission}
        shareDialogOpen={shareDialogOpen}
        sharing={sharing}
        setView={setView}
        setSelectedSubmission={setSelectedSubmission}
        setShareDialogOpen={setShareDialogOpen}
        onShareResults={openShareResults}
        onRefreshRecs={handleRefreshRecs}
        onShareSend={handleShareSend}
      />
    );
  }

  // == RESULTS VIEW ==
  if (view === 'results' && result) {
    return (
      <CapabilityResultsView
        result={result}
        pendingCertsToUpload={pendingCertsToUpload}
        myCerts={myCerts}
        certDialogOpen={certDialogOpen}
        certUploadName={certUploadName}
        certFile={certFile}
        certIssueDate={certIssueDate}
        certExpiryDate={certExpiryDate}
        certUploading={certUploading}
        datesDialogOpen={datesDialogOpen}
        editingCert={editingCert}
        editIssueDate={editIssueDate}
        editExpiryDate={editExpiryDate}
        shareDialogOpen={shareDialogOpen}
        sharing={sharing}
        setView={setView}
        setCertDialogOpen={setCertDialogOpen}
        setCertUploadName={setCertUploadName}
        setCertFile={setCertFile}
        setCertIssueDate={setCertIssueDate}
        setCertExpiryDate={setCertExpiryDate}
        setDatesDialogOpen={setDatesDialogOpen}
        setEditIssueDate={setEditIssueDate}
        setEditExpiryDate={setEditExpiryDate}
        setShareDialogOpen={setShareDialogOpen}
        onShareResults={openShareResults}
        onRefreshRecs={handleRefreshRecs}
        onOpenEditDates={openEditDates}
        onDeleteCert={handleDeleteCert}
        onCertUpload={handleCertUpload}
        onSaveDates={handleSaveDates}
        onShareSend={handleShareSend}
      />
    );
  }

  // == TAKE ASSESSMENT VIEW ==
  return (
    <CapabilityTakeAssessmentView
      selectedAssessment={selectedAssessment}
      sections={sections}
      section={section}
      currentSection={currentSection}
      answers={answers}
      answeredInSection={answeredInSection}
      totalInSection={totalInSection}
      totalAnswered={totalAnswered}
      totalQuestions={totalQuestions}
      submitting={submitting}
      setView={setView}
      setCurrentSection={setCurrentSection}
      onAnswer={handleAnswer}
      onSubmit={handleSubmit}
    />
  );
};

export default CapabilityAssessmentPage;
